import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError, authQuery, API, downloadUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../lib/confirm";
import { Plus, Search, Eye, Lock, Unlock, Trash2, FileDown, ArrowRightLeft, X, MessageSquareText } from "lucide-react";
import SendMessageModal from "../components/SendMessageModal";

const GRADES = ["Entered Apprentice", "Fellow Craft", "Master Mason", "Honorary"];
const STATUSES = ["Active", "Inactive", "Suspended", "Deceased", "Dormant", "Excluded"];

// Map raw enum values to i18next keys. The DB value stays English so analytics
// + APIs keep working; only the UI label is localized.
// Aliases supported: legacy single-word labels (`"Apprentice"` from older
// mobile builds) + Romanian names (`"Ucenic"`, `"Calfă"`, `"Maestru"`) that
// some early seed data used. Falls back to a generic "—" when nothing maps.
const GRADE_KEY = {
  "Entered Apprentice": "members.grade_entered",
  "Apprentice": "members.grade_entered",
  "Ucenic": "members.grade_entered",
  "Fellow Craft": "members.grade_fellow",
  "Calfă": "members.grade_fellow",
  "Calfa": "members.grade_fellow",
  "Master Mason": "members.grade_master",
  "Maestru": "members.grade_master",
  "Honorary": "members.grade_honorary",
  "Onorific": "members.grade_honorary",
};

// Helper that always returns a usable grade label — falls back from the
// stored `grade` to the `degree` number, then to a literal "—".
const gradeLabel = (m, t) => {
  const key = GRADE_KEY[m?.grade];
  if (key) return t(key, m.grade);
  if (m?.grade) return m.grade;
  if (m?.degree === 1) return t("members.grade_entered");
  if (m?.degree === 2) return t("members.grade_fellow");
  if (m?.degree === 3) return t("members.grade_master");
  return "—";
};
const STATUS_KEY = {
  Active: "members.status_active",
  Inactive: "members.status_inactive",
  Suspended: "members.status_suspended",
  Deceased: "members.status_deceased",
  Dormant: "members.status_dormant",
  Excluded: "members.status_excluded",
};

function gradeBadge(grade) {
  if (grade === "Master Mason" || grade === "Maestru") return "badge-grade master";
  if (grade === "Fellow Craft" || grade === "Calfă" || grade === "Calfa") return "badge-grade fellow";
  if (grade === "Honorary" || grade === "Onorific") return "badge-grade honorary";
  return "badge-grade entered";
}

function statusBadge(s) {
  return `badge-status ${(s || "Active").toLowerCase()}`;
}

export default function MembersPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [members, setMembers] = useState([]);
  const [lodge, setLodge] = useState(null);
  const [childLodges, setChildLodges] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterGrade, setFilterGrade] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");
  const [transferFor, setTransferFor] = useState(null);
  const [transferTarget, setTransferTarget] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgUnread, setMsgUnread] = useState(0);

  const load = async () => {
    try {
      // First load lodge so we can branch on `is_obedience`.
      const l = await api.get("/lodge");
      const isObed = !!l.data?.is_obedience;
      const memberReq = isObed
        ? api.get("/members", { params: { include_children: true } })
        : api.get("/members");
      const childReq = isObed
        ? api.get("/jurisdiction-lodges").catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] });
      const [m, c] = await Promise.all([memberReq, childReq]);
      setMembers(m.data);
      setLodge(l.data);
      setChildLodges(c.data || []);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (isAdmin) {
      api.get("/messages/unread-replies").then(({ data }) => setMsgUnread(data.count || 0)).catch(() => {});
    }
  }, [isAdmin]);

  const removeMember = async (m) => {
    const ok = await confirm({
      title: t("common.delete"),
      message: t("members.deleteConfirm"),
    });
    if (!ok) return;
    try {
      await api.delete(`/members/${m.id}`);
      toast.success(t("members.deletedSuccess"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadPdf = () => {
    // authQuery() already includes ?lang=<i18n.language>; no need to append.
    downloadUrl(`${API}/members/export/pdf${authQuery()}`);
  };

  const canTransfer = isAdmin && !!lodge?.is_obedience && !user?.child_lodge_id && childLodges.length > 0;

  const openTransfer = (m) => {
    setTransferFor(m);
    setTransferTarget((m.child_lodge_id || "") === "" ? (childLodges[0]?.id || "") : "");
  };

  const doTransfer = async () => {
    if (!transferFor) return;
    setTransferBusy(true);
    try {
      const { data } = await api.post(`/members/${transferFor.id}/internal-transfer`, {
        child_lodge_id: transferTarget || null,
      });
      toast.success(t("members.transferSuccess", { lodge: data.to }));
      setTransferFor(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTransferBusy(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return members.filter((m) => {
      const name = `${m.first_name || ""} ${m.last_name || ""}`.toLowerCase();
      if (q && !name.includes(q) && !(m.email || "").toLowerCase().includes(q)) return false;
      if (filterGrade !== "All" && m.grade !== filterGrade) return false;
      if (filterStatus !== "All" && m.status !== filterStatus) return false;
      return true;
    });
  }, [members, search, filterGrade, filterStatus]);

  // Obedience view: group members by `child_lodge_id`. Members without a
  // child id belong to the parent obedience itself (Grand Lodge level).
  const isObedienceView = !!lodge?.is_obedience;
  const groupedByLodge = useMemo(() => {
    if (!isObedienceView) return null;
    const groups = { __parent__: [] };
    for (const m of filtered) {
      const k = m.child_lodge_id || "__parent__";
      if (!groups[k]) groups[k] = [];
      groups[k].push(m);
    }
    const sublodgeName = (id) => {
      const sub = childLodges.find((c) => c.id === id);
      if (!sub) return t("members.unknownSublodge", "Unknown sub-lodge");
      const num = sub.number ? ` ${sub.number}` : "";
      return `${sub.name}${num}`;
    };
    const result = [];
    // Parent obedience block first
    if (groups.__parent__.length > 0) {
      result.push({
        key: "__parent__",
        label: `${lodge?.name || t("members.title")} · ${t("members.obedienceLevel", "Obedience members")}`,
        rows: groups.__parent__,
      });
    }
    // Then one block per child lodge (sorted alphabetically by name)
    const childIds = Object.keys(groups).filter((k) => k !== "__parent__");
    childIds.sort((a, b) => sublodgeName(a).localeCompare(sublodgeName(b)));
    for (const cid of childIds) {
      result.push({
        key: cid,
        label: sublodgeName(cid),
        rows: groups[cid],
      });
    }
    return result;
  }, [filtered, isObedienceView, childLodges, lodge, t]);

  return (
    <div data-testid="members-page">
      {msgOpen && (
        <SendMessageModal
          members={members}
          childLodges={childLodges}
          canPickLodge={isAdmin && !!lodge?.is_obedience && !user?.child_lodge_id && childLodges.length > 0}
          onRepliesSeen={() => setMsgUnread(0)}
          onClose={() => setMsgOpen(false)}
        />
      )}
      <h1 className="page-title">{t("members.title")}</h1>
      <p className="page-subtitle">{lodge?.name || "—"}</p>

      <div className="gold-divider my-8" />

      {error && <div className="error-banner mb-4">{error}</div>}

      {/* Top row */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-5">
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <button className="btn-primary-gold flex items-center gap-2" onClick={() => navigate("/members/new")} data-testid="add-member-btn">
              <Plus size={14} /> {t("members.newMember")}
            </button>
          )}
          {isAdmin && (
            <button className="btn-outline-gold flex items-center gap-2" onClick={() => setMsgOpen(true)} data-testid="send-message-btn" style={{ position: "relative" }}>
              <MessageSquareText size={14} /> {t("members.msgBtn", "Trimite mesaj")}
              {msgUnread > 0 && (
                <span data-testid="msg-unread-badge" style={{
                  position: "absolute", top: -7, right: -7, background: "#8a2a2a", color: "#fff",
                  borderRadius: 999, minWidth: 18, height: 18, display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: "0.62rem", fontWeight: 800, padding: "0 5px",
                }}>{msgUnread}</span>
              )}
            </button>
          )}
          {members.length > 0 && (
            <button className="btn-outline-gold flex items-center gap-2" onClick={downloadPdf} data-testid="export-pdf-btn">
              <FileDown size={14} /> {t("members.exportPdf")}
            </button>
          )}
        </div>
        <div className="relative flex-1 max-w-md">
          <Search size={16} style={{ position: "absolute", left: 14, top: 14, color: "#858481" }} />
          <input
            className="input-lodge"
            style={{ paddingLeft: "2.4rem" }}
            placeholder={t("members.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="members-search"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mb-5">
        <div>
          <div className="kv-label mb-2">{t("members.grade")}</div>
          <div className="flex flex-wrap gap-2">
            <button className={`filter-chip ${filterGrade === "All" ? "active" : ""}`} onClick={() => setFilterGrade("All")}>{t("common.all")}</button>
            {GRADES.map((g) => (
              <button key={g} className={`filter-chip ${filterGrade === g ? "active" : ""}`} onClick={() => setFilterGrade(g)}>{t(GRADE_KEY[g], g)}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="kv-label mb-2">{t("members.status")}</div>
          <div className="flex flex-wrap gap-2">
            <button className={`filter-chip ${filterStatus === "All" ? "active" : ""}`} onClick={() => setFilterStatus("All")}>{t("common.all")}</button>
            {STATUSES.map((s) => (
              <button key={s} className={`filter-chip ${filterStatus === s ? "active" : ""}`} onClick={() => setFilterStatus(s)}>{t(STATUS_KEY[s], s)}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="table-wrap">
      {isObedienceView && groupedByLodge ? (
        // Obedience view: render ONE table per sub-lodge (+ parent block).
        groupedByLodge.map((grp) => (
          <div key={grp.key} style={{ marginBottom: "2.5rem" }} data-testid={`members-group-${grp.key}`}>
            <h3 style={{
              fontFamily: "Cinzel, serif",
              fontSize: "1.05rem",
              color: "#c9a84c",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              borderBottom: "1px solid rgba(201,168,76,0.35)",
              paddingBottom: "0.5rem",
              marginBottom: "0.9rem",
            }}>
              {grp.label} <span style={{ color: "#858481", fontSize: "0.75rem", marginLeft: 8 }}>({grp.rows.length})</span>
            </h3>
            {renderMembersTable(grp.rows)}
          </div>
        ))
      ) : (
        renderMembersTable(filtered)
      )}
      </div>

      {transferFor && (
        <div className="modal-overlay" onClick={() => setTransferFor(null)}>
          <div className="modal-content gold-card" style={{ maxWidth: 480, padding: "1.4rem" }} onClick={(e) => e.stopPropagation()} data-testid="transfer-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.14em", color: "#0d1b2a" }}>
                {t("members.transferTitle", "Transferă în altă lojă")}
              </h3>
              <button onClick={() => setTransferFor(null)} className="text-gray-500" data-testid="transfer-modal-close"><X size={18} /></button>
            </div>
            <p className="kv-label" style={{ fontSize: "0.72rem", marginBottom: 12 }}>
              {transferFor.first_name} {transferFor.last_name}
            </p>
            <label className="label-lodge">{t("members.transferDest", "Loja destinație")}</label>
            <select className="input-lodge" value={transferTarget} onChange={(e) => setTransferTarget(e.target.value)} data-testid="transfer-dest-select">
              {(transferFor.child_lodge_id || "") !== "" && (
                <option value="">{lodge?.name} · {t("members.obedienceLevel", "Obedience members")}</option>
              )}
              {childLodges.filter((c) => c.id !== (transferFor.child_lodge_id || "")).map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.number ? ` ${c.number}` : ""}</option>
              ))}
            </select>
            <p className="page-subtitle" style={{ fontSize: "0.78rem", marginTop: 10 }}>
              {t("members.transferNote", "Contul de aplicație mobilă, pașaportul, NFC-ul și documentele rămân neschimbate. Membrul primește notificare în aplicație.")}
            </p>
            <div className="gold-divider my-4" />
            <button className="btn-primary-gold flex items-center gap-2" onClick={doTransfer} disabled={transferBusy} data-testid="transfer-confirm-btn">
              <ArrowRightLeft size={14} /> {transferBusy ? "..." : t("members.transferBtn", "Transferă")}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ─── Inline renderer for the members table. Re-used by both the
  //     standard view and the obedience grouped view (one table per
  //     sub-lodge).
  function renderMembersTable(rows) {
    return (
      <table className="lodge-table" data-testid="members-table">
        <thead>
          <tr>
            <th>{t("common.name")}</th>
            <th data-mobile-hide="true">{t("members.degree")}</th>
            <th>{t("common.status")}</th>
            <th data-mobile-hide="true">{t("members.initiated")}</th>
            <th data-mobile-hide="true">{t("members.private")}</th>
            <th style={{ textAlign: "right" }}>{t("common.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={6} style={{ textAlign: "center", fontStyle: "italic", color: "#858481", padding: "2rem" }}>
                {t("members.noResults")}
              </td>
            </tr>
          ) : (
            rows.map((m) => (
              <tr key={m.id} data-testid={`member-row-${m.id}`} style={{ cursor: "pointer" }} onClick={() => navigate(`/members/${m.id}`)}>
                <td>
                  <div className="flex items-center gap-3">
                    {m.photo_url ? (
                      <img src={`${API}/photo?path=${encodeURIComponent(m.photo_url)}${authQuery().replace("?", "&")}`} alt="" style={{ width: 36, height: 36, objectFit: "cover", border: "1px solid #c9a84c" }} />
                    ) : (
                      <div style={{ width: 36, height: 36, background: "#f5f0e8", border: "1px solid #ede5d0", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Cinzel, serif", color: "#9a7530", fontSize: "0.75rem" }}>
                        {(m.first_name?.[0] || "") + (m.last_name?.[0] || "")}
                      </div>
                    )}
                    <div>
                      <div style={{ fontFamily: "Cinzel, serif", fontSize: "0.95rem", color: "#0d1b2a" }}>{m.first_name} {m.last_name}</div>
                      {m.profession && <div style={{ fontSize: "0.8rem", color: "#858481", fontStyle: "italic" }}>{m.profession}</div>}
                    </div>
                  </div>
                </td>
                <td data-mobile-hide="true"><span className={gradeBadge(m.grade)}>{gradeLabel(m, t)}</span>{m.degree && <span className="kv-label" style={{ marginLeft: 8 }}>· {m.degree}</span>}</td>
                <td><span className={statusBadge(m.status)}>{t(STATUS_KEY[m.status || "Active"], m.status || "Active")}</span></td>
                <td data-mobile-hide="true">{m.date_initiated || "—"}</td>
                <td data-mobile-hide="true">{m.is_private ? <Lock size={14} color="#9a7530" /> : <Unlock size={14} color="#858481" />}</td>
                <td style={{ textAlign: "right" }}>
                  <div className="flex gap-2" style={{ justifyContent: "flex-end" }}>
                    <button className="btn-outline-gold flex items-center gap-1" onClick={(e) => { e.stopPropagation(); navigate(`/members/${m.id}`); }} data-testid={`view-member-${m.id}`}>
                      <Eye size={12} /> {t("common.details")}
                    </button>
                    {canTransfer && (
                      <button className="btn-outline-gold flex items-center gap-1"
                        title={t("members.transferTitle", "Transferă în altă lojă")}
                        onClick={(e) => { e.stopPropagation(); openTransfer(m); }}
                        data-testid={`transfer-member-${m.id}`}>
                        <ArrowRightLeft size={12} />
                      </button>
                    )}
                    {isAdmin && (
                      <button className="btn-danger flex items-center gap-1" onClick={(e) => { e.stopPropagation(); removeMember(m); }} data-testid={`delete-member-${m.id}`}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    );
  }
}
